"use client";

import * as React from "react";
import { Check, X, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Badge } from "../../components/ui/badge";

// Local type for the combobox
interface DataType {
  id: string;
  name: string;
  description?: string;
  originalDataType: any;
}

// Props for the DataTypeCombobox component
interface DataTypeComboboxProps {
  selected: string[];
  setSelected: (values: string[] | ((prev: string[]) => string[])) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  onDataTypeSelect?: (dataTypeId: string) => void;
  onDataTypeDeselect?: (dataTypeId: string) => void;
  onAllSelect?: () => void;
  onAllDeselect?: () => void;
  dataTypes?: DataType[]; // Optionally pass data types directly
  fetchDataTypes?: () => Promise<DataType[]>; // Or provide a function to fetch them
}

import { getDataTypes } from "@/services/dataRetentionService";
// Context for providing data types
interface DataTypesContextType {
  dataTypes: DataType[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<DataType[]>;
  searchDataTypes: (query: string) => Promise<void>;
}

const DataTypesContext = React.createContext<DataTypesContextType>({
  dataTypes: [],
  loading: false,
  error: null,
  refresh: async () => [],
  searchDataTypes: async () => {},
});

// Provider component to fetch and manage data types
interface DataTypesProviderProps {
  children: React.ReactNode;
}

export const DataTypesProvider: React.FC<DataTypesProviderProps> = ({ children }) => {
  const [dataTypes, setDataTypes] = React.useState<DataType[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getDataTypes();
      const mapped = result.map(dt => ({
        id: String(dt.dataTypeID),
        name: dt.dataTypeName,
        description: dt.dataTypeJustification,
        originalDataType: dt
      }));
      setDataTypes(mapped);
      setLoading(false);
      return mapped;
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      return [];
    }
  }, []);

  const searchDataTypes = async (query: string) => {
    setDataTypes(prev => prev.filter(t => t.name.toLowerCase().includes(query.toLowerCase())));
  };

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <DataTypesContext.Provider value={{ dataTypes, loading, error, refresh, searchDataTypes }}>
      {children}
    </DataTypesContext.Provider>
  );
};

export function useDataTypes(): DataTypesContextType {
  return React.useContext(DataTypesContext);
}

// Main DataTypeCombobox component
const DataTypeCombobox: React.FC<DataTypeComboboxProps> = ({
  selected,
  setSelected,
  placeholder = "Select data types...",
  className,
  fullWidth = false,
  onDataTypeSelect,
  onDataTypeDeselect,
  onAllSelect,
  onAllDeselect,
}) => {
  const context = useDataTypes();
  const dataTypes = context.dataTypes;
  const loading = context.loading;

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const filteredDataTypes = search
    ? dataTypes.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
    : dataTypes;

  const handleSelect = (id: string) => {
    if (!selected.includes(id)) {
      setSelected([...selected, id]);
      onDataTypeSelect?.(id);
    }
  };

  const handleDeselect = (id: string) => {
    setSelected(selected.filter((s) => s !== id));
    onDataTypeDeselect?.(id);
  };

  const handleClear = () => {
    setSelected([]);
    onAllDeselect?.();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-auto hover:bg-transparent",
            fullWidth && "w-full",
            className
          )}
        >
          {selected.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            <div className="flex flex-wrap gap-1 h-auto max-h-auto overflow-y-auto">
              {selected.map((id) => {
                const dt = dataTypes.find((t) => t.id === id);
                return (
                  <div
                    key={id}
                    className="flex items-center gap-1 bg-primary/10 text-primary rounded-md px-2 py-1 text-sm hover:bg-primary/20"
                  >
                    <span>{dt?.name || id}</span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeselect(id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDeselect(id);
                        }
                      }}
                      className="ml-1 opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
                    >
                      <X className="h-3 w-3" />
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0">
        <Command>
          <CommandInput
            placeholder="Search data types..."
            value={search}
            onValueChange={setSearch}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>No data types found.</CommandEmpty>
            <CommandGroup>
              {filteredDataTypes.map((dt) => (
                <CommandItem
                  key={dt.id}
                  value={dt.id}
                  onSelect={() =>
                    selected.includes(dt.id)
                      ? handleDeselect(dt.id)
                      : handleSelect(dt.id)
                  }
                  className={selected.includes(dt.id) ? "bg-accent" : ""}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.includes(dt.id) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span>{dt.name}</span>
                  {dt.description && (
                    <span className="ml-2 text-xs text-muted-foreground">{dt.description}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        <div className="flex justify-between items-center px-2 py-2 border-t mt-2">
          <Button size="sm" variant="ghost" onClick={handleClear}>
            Clear
          </Button>
          <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DataTypeCombobox;
export { DataTypeCombobox };